/**
 * A component with common layout elements for all routes.
 */
import '#/bemComponents' // importing it so it exists

import { useDisclosure } from '@mantine/hooks'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'

import { MantineProvider } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'
import { QueryClientProvider } from '@tanstack/react-query'
import DocumentTitle from 'react-document-title'
import reactMixin from 'react-mixin'
import { Outlet } from 'react-router-dom'
import { queryClient } from '#/api/queryClient'
import AuthChangeRedirector from '#/auth/AuthChangeRedirector'
import ProfileDetailsBlocker from '#/auth/ProfileDetailsBlocker/ProfileDetailsBlocker'
import ProfileDetailsErrorScreen from '#/auth/ProfileDetailsBlocker/ProfileDetailsErrorScreen'
import { useProfileDetailsBlockerState } from '#/auth/ProfileDetailsBlocker/useProfileDetailsBlockerState'
import bem from '#/bem'
import Drawer from '#/components/Drawer'
import BigModal from '#/components/bigModal/bigModal'
import LoadingSpinner from '#/components/common/loadingSpinner'
import MainHeader from '#/components/header/mainHeader.component'
import { isAnyProcessingRouteActive } from '#/components/processing/routes.utils'
import envStore from '#/envStore'
import mixins from '#/mixins'
import pageState from '#/pageState.store'
import FormViewSideTabs from '#/project/formViewSideTabs'
import ProjectTopTabs from '#/project/projectTopTabs.component'
import { RootContextProvider } from '#/rootContextProvider.component'
import InvalidatedPassword from '#/router/invalidatedPassword.component'
import {
  getRouteAssetUid,
  isInvalidatedPasswordRouteBlockerActive,
  isTOSAgreementRouteBlockerActive,
} from '#/router/routerUtils'
import TOSAgreement from '#/router/tosAgreement.component'
import CloseModalsOnNavigate from './router/CloseModalsOnNavigate'
import { router, withRouter } from './router/legacy'
import { Tracking } from './router/useTracking'
import { cssVariablesResolverKobo, themeKobo } from './theme'
import { KOBO_MODAL_SHARED_PROPS } from './theme/kobo/Modal'
import ToasterConfig from './toasterConfig'

import './api/mutation-defaults'

/**
 * Renders the page necessities (wrapper modifiers, BigModal, header, drawer, content area). Extracted from App because
 * it needs hooks for modal subscription and drawer state.
 */
function AppPageWrapper({ shouldDisplayMain, inFormBuilder, isFormSingle, isLibrarySingle, assetUid }) {
  const [modal, setModal] = useState(() => pageState.state.modal ?? false)
  const [isDrawerOpen, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false)

  // Subscribe to modal state changes.
  useEffect(() => {
    return pageState.listen((state) => setModal(state.modal ?? false))
  }, [])

  // Close the drawer on every route change (better mobile experience).
  useEffect(() => {
    return router.subscribe(() => {
      closeDrawer()
    })
  }, [closeDrawer])

  const pageWrapperContentModifiers = []
  if (isFormSingle) {
    pageWrapperContentModifiers.push('form-landing')
  }
  if (isLibrarySingle) {
    pageWrapperContentModifiers.push('library-landing')
  }

  const pageWrapperModifiers = {
    'fixed-drawer': isDrawerOpen,
    'in-formbuilder': inFormBuilder,
    'is-modal-visible': Boolean(modal),
  }

  if (typeof modal === 'object') {
    pageWrapperModifiers[`is-modal-${modal.type}`] = true
  }

  return (
    <>
      {shouldDisplayMain && <div className='header-stretch-bg' />}

      <bem.PageWrapper m={pageWrapperModifiers} className='mdl-layout mdl-layout--fixed-header'>
        {modal && <BigModal params={modal} />}

        {shouldDisplayMain && (
          <>
            <MainHeader assetUid={assetUid} onToggleMobileMenu={toggleDrawer} />
            <Drawer />
          </>
        )}

        <bem.PageWrapper__content className='mdl-layout__content' m={pageWrapperContentModifiers}>
          {shouldDisplayMain && (
            <>
              {isFormSingle && <ProjectTopTabs />}
              <FormViewSideTabs show={isFormSingle} />
            </>
          )}

          <Outlet />
        </bem.PageWrapper__content>
      </bem.PageWrapper>
    </>
  )
}

/**
 * The route blockers, and the app itself when none of them applies. An active blocker takes the place of the whole
 * page (see `isAnyRouteBlockerActive`), and the order below is the order they get their turn.
 *
 * Only the app branch gets `RootContextProvider`: its billing requests are for the account routes, and one of them
 * (`/stripe/addons/`) answers 403 to exactly the user `InvalidatedPassword` is up for.
 *
 * Observes the stores it asks, because the answers change as the session and `/environment` land.
 */
const AppGuard = observer(function AppGuard({ shouldDisplayMain, inFormBuilder, isFormSingle, isLibrarySingle }) {
  // Before the early returns, so the hook order stays the same on every render.
  const profileDetails = useProfileDetailsBlockerState()

  // Two of the three answers below come from `/environment`, and nothing in the app works without it anyway
  // - so hold everything back rather than show the app and take it away a moment later.
  if (!envStore.isReady) {
    return <LoadingSpinner />
  }

  if (isInvalidatedPasswordRouteBlockerActive()) {
    return <InvalidatedPassword />
  }

  if (isTOSAgreementRouteBlockerActive()) {
    return <TOSAgreement />
  }

  // Three branches because the organization request can leave this undecided: pending waits, failed gets a
  // screen with a way out rather than an endless spinner.
  if (profileDetails.status === 'pending') {
    return <LoadingSpinner />
  }

  if (profileDetails.status === 'error') {
    return <ProfileDetailsErrorScreen onRetry={() => window.location.reload()} />
  }

  if (profileDetails.status === 'active') {
    return <ProfileDetailsBlocker isMmoMember={profileDetails.isMmoMember} />
  }

  // TODO: We have multiple routes that shouldn't display `MainHeader`,
  // `Drawer`, `ProjectTopTabs` etc. Instead of relying on CSS via
  // `pageWrapperModifiers`, or `show` properties, or JSX logic - we should
  // opt for a more sane, and singular(!) solution.
  return (
    <RootContextProvider>
      <AppPageWrapper
        shouldDisplayMain={shouldDisplayMain}
        inFormBuilder={inFormBuilder}
        isFormSingle={isFormSingle}
        isLibrarySingle={isLibrarySingle}
        assetUid={getRouteAssetUid()}
      />
    </RootContextProvider>
  )
})

class App extends React.Component {
  constructor(props) {
    super(props)
    this.state = {}
  }

  /** Whether to display the top header navigation and the side menu. */
  shouldDisplayMainLayoutElements() {
    return (
      // Hide in Form Builder
      !this.isFormBuilder() &&
      // Hide in Single Processing View
      !isAnyProcessingRouteActive()
    )
  }

  render() {
    // The UI and query providers wrap the route blockers too, so a blocker screen gets the same theme, toasts and
    // query client as the app. `RootContextProvider` is the exception - see `AppGuard`.
    return (
      <DocumentTitle title='KoboToolbox'>
        <QueryClientProvider client={queryClient}>
          {/* Outside the guards below on purpose */}
          <AuthChangeRedirector />

          <MantineProvider theme={themeKobo} cssVariablesResolver={cssVariablesResolverKobo}>
            <Notifications />
            <ModalsProvider modalProps={KOBO_MODAL_SHARED_PROPS}>
              <Tracking />
              <CloseModalsOnNavigate />
              <ToasterConfig />

              <AppGuard
                shouldDisplayMain={this.shouldDisplayMainLayoutElements()}
                inFormBuilder={this.isFormBuilder()}
                isFormSingle={this.isFormSingle()}
                isLibrarySingle={this.isLibrarySingle()}
              />
            </ModalsProvider>
          </MantineProvider>

          {/* React Query Devtools - GUI for inspecting and modifying query status
              (https://tanstack.com/query/latest/docs/framework/react/devtools)
              They only show up in dev server (NODE_ENV==='development')
              Additionally, we're keeping them commented out in `beta`
              (https://github.com/kobotoolbox/kpi/pull/5001#discussion_r1691067344)
                (1) Uncomment if you want to use these tools
                (2) The <style> tag lowers the toggle button opacity
                    to make it less prominent in dev screenshots. */}
          {/*
            <style>{'.tsqd-open-btn-container { opacity: 0.1 !important; };'}</style>
            <ReactQueryDevtools />
          */}
        </QueryClientProvider>
      </DocumentTitle>
    )
  }
}

reactMixin(App.prototype, mixins.contextRouter)

export default withRouter(App)
